import { useEffect, useState } from "react";
import { Box, Button, InputAdornment, MenuItem, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import dayjs, { Dayjs } from "dayjs";
import axios from "../../api/axios";
import { useCRUD } from "../../api/crud";
import { useAuthContext } from "../../contexts/auth";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import CustomDatePicker from "../ui/customDatePicker/customDatePicker.component";
import CustomAutocomplete, { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import {
  ORDERED_PARTS_ENDPOINT,
  PARTS_ENDPOINT,
  NUMERATION_SCHEMA_ENDPOINT,
  USERS_ENDPOINT,
  TENANT_SETTINGS_ENDPOINT,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
} from "../../consts";
import {
  DECIMAL_INPUT_REGEX,
  formatDecimalForDisplay,
  formatDecimalForInput,
  formatMoney,
  toApiDecimal,
} from "../../utils";

// "Used" isn't selectable here - the backend sets it automatically once the
// full ordered quantity has been used up (see UsedPart creation logic).
const SELECTABLE_STATUS_OPTIONS: { value: string; labelKey: string }[] = [
  { value: "O", labelKey: "Ordered" },
  { value: "D", labelKey: "Delivered" },
];
const STATUS_LABEL_KEYS: Record<string, string> = {
  O: "Ordered",
  D: "Delivered",
  U: "Used",
};
// New orders start at the standard Polish rate; an existing one shows whatever
// rate it was saved with.
const DEFAULT_VAT = "23";

interface OrderedPartFormValues {
  numeration_schema_id: OptionRecord | null;
  status: string;
  number: string;
  delivered_at: string;
  quantity: string;
  price_net: string;
  vat: string;
  ordered_by_id: OptionRecord | null;
}

const buildDefaultValues = (): OrderedPartFormValues => ({
  numeration_schema_id: null,
  status: "O",
  number: "",
  delivered_at: "",
  quantity: "",
  price_net: "",
  vat: DEFAULT_VAT,
  ordered_by_id: null,
});

const extractErrorMessage = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (!first) return null;
    if (typeof first === "string") return first;
    if (typeof first === "object" && first && "message" in first) {
      return (first as { message?: string }).message ?? null;
    }
  }
  if (typeof value === "object" && value && "message" in value) {
    return (value as { message?: string }).message ?? null;
  }
  return null;
};

interface OrderedPartFormProps {
  id?: string;
  /** Locks the "Part" field to this id - used when adding/editing from within a specific Part's page. */
  initialPartId?: string;
  onSuccess?: (data?: any) => void;
}

export default function OrderedPartForm({ id, initialPartId, onSuccess }: OrderedPartFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  const { get, create, update, loading } = useCRUD(ORDERED_PARTS_ENDPOINT);
  const { get: getPart } = useCRUD(PARTS_ENDPOINT);
  const [formData, setFormData] = useState<OrderedPartFormValues>(buildDefaultValues);
  const [partId, setPartId] = useState<string | number | null>(initialPartId ?? null);
  const [partName, setPartName] = useState<string>("");
  const [serverErrors, setServerErrors] = useState<any>(null);
  const [loadingInitial, setLoadingInitial] = useState(!!id);
  const [numberLoading, setNumberLoading] = useState(false);
  // Which money field is being typed in. The grouped "1 234,56" form is only
  // shown while a field is idle - mid-edit it would reposition the caret on
  // every keystroke, so the focused field falls back to the raw value.
  const [focusedDecimalField, setFocusedDecimalField] = useState<string | null>(null);

  // The part an ordered part belongs to is never reassignable through this
  // form - it's locked whenever we're editing an existing record, or adding
  // one from within a specific Part's page.
  const isPartLocked = Boolean(initialPartId) || Boolean(id);

  useEffect(() => {
    if (!id) {
      // Create mode: default "ordered by" to the current user.
      setFormData((prev) => ({
        ...prev,
        ordered_by_id: currentUser
          ? ({
            id: currentUser.id,
            full_name: [currentUser.first_name, currentUser.last_name].filter(Boolean).join(" "),
            email: currentUser.email,
          } as OptionRecord)
          : null,
      }));
      setLoadingInitial(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      const response = await get(id);
      if (cancelled) return;
      if (response?.data) {
        const d = response.data;
        setPartId(d.part ?? initialPartId ?? null);
        setPartName(d.part_name ?? "");
        setFormData({
          numeration_schema_id: d.numeration_schema ?? null,
          status: d.status ?? "O",
          number: d.number ?? "",
          delivered_at: d.delivered_at ?? "",
          quantity: formatDecimalForInput(d.quantity),
          price_net: formatDecimalForInput(d.price_net),
          vat: formatDecimalForInput(d.vat) || DEFAULT_VAT,
          ordered_by_id: d.ordered_by ?? null,
        });
      }
      setLoadingInitial(false);
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Locked-but-unknown-name case (adding from within a Part's page): fetch its
  // name once just for display - editing already gets it from the ordered part itself.
  useEffect(() => {
    if (!initialPartId || id || partName) return;

    let cancelled = false;
    const load = async () => {
      const response = await getPart(initialPartId);
      if (!cancelled && response?.data?.name) {
        setPartName(response.data.name);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [initialPartId, id, partName, getPart]);

  // Create mode only: prefill the numeration schema configured as the
  // workspace default for ordered parts, mirroring JobForm's behavior.
  useEffect(() => {
    if (id) return;

    let cancelled = false;
    const prefillNumerationSchema = async () => {
      try {
        const { data: tenantSettings } = await axios.get(`${TENANT_SETTINGS_ENDPOINT}settings/`);
        const defaultSchemaId = tenantSettings?.default_ordered_part_numeration_schema;
        if (cancelled || !defaultSchemaId) return;

        const { data: schema } = await axios.get(`${NUMERATION_SCHEMA_ENDPOINT}${defaultSchemaId}/`);
        if (cancelled || !schema) return;

        setFormData((prev) => (prev.numeration_schema_id ? prev : { ...prev, numeration_schema_id: schema }));
        fetchNextNumber(schema.id);
      } catch (err) {
        // Tenant settings or default schema might not be found - silent fail.
      }
    };
    prefillNumerationSchema();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getFieldError = (field: string): string | null =>
    extractErrorMessage(serverErrors?.[field]);

  const fetchNextNumber = async (schemaId: string | number) => {
    setNumberLoading(true);
    try {
      const { data } = await axios.get(`${NUMERATION_SCHEMA_ENDPOINT}${schemaId}/next/`);
      const nextValue = data?.value ?? data?.result ?? data?.number ?? data;
      setFormData((prev) => ({ ...prev, number: nextValue ? String(nextValue) : "" }));
    } catch (err) {
      console.error("Failed to fetch next ordered part number", err);
    } finally {
      setNumberLoading(false);
    }
  };

  const handleDecimalChange = (field: "quantity" | "price_net" | "vat") => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Strip any grouping first: a value pasted straight from a grouped display
    // ("1 234,56") would otherwise fail the plain-decimal check below.
    const nextValue = e.target.value.replace(/[\s\u00A0\u202F]/g, "");
    if (nextValue === "" || DECIMAL_INPUT_REGEX.test(nextValue)) {
      setFormData((prev) => ({ ...prev, [field]: nextValue }));
    }
  };

  /** Field props for an amount shown grouped ("1 234,56") when idle and raw
   * while being typed in. */
  const groupedDecimalFieldProps = (field: "price_net" | "vat") => ({
    value:
      focusedDecimalField === field
        ? formData[field]
        : formatDecimalForDisplay(formData[field]),
    onChange: handleDecimalChange(field),
    onFocus: () => setFocusedDecimalField(field),
    onBlur: () => setFocusedDecimalField(null),
    // The idle value carries grouping spaces, so the pattern has to admit them
    // - it describes what the field can hold, not just what can be typed.
    inputProps: { inputMode: "decimal" as const, pattern: "[0-9\\s\u00A0\u202F]*[.,]?[0-9]*" },
  });

  const handleSubmit = async () => {
    setServerErrors(null);
    const payload = {
      part: partId ?? undefined,
      numeration_schema_id: formData.numeration_schema_id?.id || undefined,
      status: formData.status || undefined,
      number: formData.number || undefined,
      delivered_at: formData.delivered_at || undefined,
      quantity: formData.quantity ? toApiDecimal(formData.quantity) : undefined,
      price_net: formData.price_net ? toApiDecimal(formData.price_net) : undefined,
      vat: formData.vat ? toApiDecimal(formData.vat) : undefined,
      ordered_by_id: formData.ordered_by_id?.id || undefined,
    };

    const response = id ? await update({ id, ...payload }) : await create(payload);

    if (response?.success) {
      enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
      onSuccess?.(response.data);
    } else {
      setServerErrors(response?.errorData);
      const failureMessage =
        extractErrorMessage(response?.errorData?.message) ||
        extractErrorMessage(response?.errorData?.detail) ||
        t("There was an error, please review the form.");
      enqueueSnackbar(failureMessage, ERROR_SNACKBAR);
    }
  };

  if (loadingInitial) return <CustomCircularProgress />;

  const fieldBoxSx = {
    flex: { xs: "1 1 100%", sm: "1 1 45%" },
    maxWidth: { xs: "100%", sm: "calc(50% - 8px)" },
  };
  const isNumberDisabled = Boolean(formData.numeration_schema_id) || numberLoading;
  // Mirrors the total the backend stores (price_net * (1 + vat/100) * quantity)
  // so the cost of the order is visible before it's submitted, rather than only
  // once it shows up in the part's table.
  const parsedQuantity = Number(toApiDecimal(formData.quantity));
  const parsedPriceNet = Number(toApiDecimal(formData.price_net));
  const parsedVat = Number(toApiDecimal(formData.vat));
  const hasTotalPreview =
    Number.isFinite(parsedQuantity) &&
    Number.isFinite(parsedPriceNet) &&
    Number.isFinite(parsedVat) &&
    formData.quantity !== "" &&
    formData.price_net !== "";
  const totalNet = parsedPriceNet * parsedQuantity;
  const totalGross = totalNet * (1 + parsedVat / 100);
  // "Used" is only reachable automatically (never user-selected), so keep the
  // field disabled once it's there rather than offering a way back out of it.
  const isStatusAlreadyUsed = formData.status === "U";
  const statusOptions = isStatusAlreadyUsed
    ? [...SELECTABLE_STATUS_OPTIONS, { value: "U", labelKey: STATUS_LABEL_KEYS.U }]
    : SELECTABLE_STATUS_OPTIONS;
  const showDeliveredAt = formData.status === "D";

  const handleStatusChange = (nextStatus: string) => {
    setFormData((prev) => ({
      ...prev,
      status: nextStatus,
      delivered_at:
        nextStatus === "D" ? prev.delivered_at || dayjs().format("YYYY-MM-DD") : "",
    }));
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Box sx={fieldBoxSx}>
        {isPartLocked ? (
          <TextField
            size="small"
            label={t("Part")}
            value={partName || "…"}
            disabled
            fullWidth
          />
        ) : (
          <CustomAutocomplete
            size="small"
            apiConfig={{ url: PARTS_ENDPOINT }}
            value={partId ? ({ id: partId, name: partName } as OptionRecord) : null}
            onChange={(_, newValue) => {
              setPartId(newValue ? newValue.id : null);
              setPartName(newValue ? newValue.name : "");
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Part")}
                required
                fullWidth
                error={Boolean(getFieldError("part"))}
                helperText={getFieldError("part") || ""}
              />
            )}
          />
        )}
      </Box>

      <Box sx={fieldBoxSx}>
        <TextField
          select
          size="small"
          label={t("Status")}
          value={formData.status}
          onChange={(e) => handleStatusChange(e.target.value)}
          required
          fullWidth
          disabled={isStatusAlreadyUsed}
          error={Boolean(getFieldError("status"))}
          helperText={getFieldError("status") || ""}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={fieldBoxSx}>
        <CustomAutocomplete
          size="small"
          apiConfig={{ url: NUMERATION_SCHEMA_ENDPOINT }}
          value={formData.numeration_schema_id}
          onChange={(_, newValue) => {
            setFormData((prev) => ({
              ...prev,
              numeration_schema_id: newValue,
              number: newValue ? prev.number : "",
            }));
            if (newValue) fetchNextNumber(newValue.id);
          }}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Numeration schema")}
              fullWidth
              error={Boolean(getFieldError("numeration_schema_id"))}
              helperText={getFieldError("numeration_schema_id") || ""}
            />
          )}
        />
      </Box>

      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Number")}
          value={formData.number}
          onChange={(e) => setFormData((prev) => ({ ...prev, number: e.target.value }))}
          fullWidth
          disabled={isNumberDisabled}
          InputProps={{
            endAdornment: numberLoading ? (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {t("Loading...")}
              </Typography>
            ) : undefined,
          }}
          error={Boolean(getFieldError("number"))}
          helperText={getFieldError("number") || ""}
        />
      </Box>

      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Price Net")}
          {...groupedDecimalFieldProps("price_net")}
          required
          fullWidth
          error={Boolean(getFieldError("price_net"))}
          helperText={getFieldError("price_net") || ""}
        />
      </Box>

      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("VAT")}
          {...groupedDecimalFieldProps("vat")}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          required
          fullWidth
          error={Boolean(getFieldError("vat"))}
          helperText={getFieldError("vat") || ""}
        />
      </Box>

      <Box sx={fieldBoxSx}>
        <TextField
          size="small"
          label={t("Quantity")}
          value={formData.quantity}
          onChange={handleDecimalChange("quantity")}
          required
          fullWidth
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          error={Boolean(getFieldError("quantity"))}
          helperText={getFieldError("quantity") || ""}
        />
      </Box>

      <Box sx={fieldBoxSx}>
        <CustomAutocomplete
          size="small"
          apiConfig={{ url: USERS_ENDPOINT }}
          value={formData.ordered_by_id}
          onChange={(_, newValue) =>
            setFormData((prev) => ({ ...prev, ordered_by_id: newValue }))
          }
          getOptionLabel={(option) => option.full_name || option.email || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Ordered By")}
              required
              fullWidth
              error={Boolean(getFieldError("ordered_by_id"))}
              helperText={getFieldError("ordered_by_id") || ""}
            />
          )}
        />
      </Box>

      {/* Kept last so its conditional appearance never shifts the fields above. */}
      {showDeliveredAt && (
        <Box sx={fieldBoxSx}>
          <CustomDatePicker
            label={t("Delivered At")}
            value={formData.delivered_at ? dayjs(formData.delivered_at) : null}
            onChange={(value: Dayjs | null) =>
              setFormData((prev) => ({
                ...prev,
                delivered_at: value ? value.format("YYYY-MM-DD") : "",
              }))
            }
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: Boolean(getFieldError("delivered_at")),
                helperText: getFieldError("delivered_at") || "",
              },
            }}
          />
        </Box>
      )}

      {hasTotalPreview && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 2,
            flexWrap: "wrap",
            px: 1.5,
            py: 1,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("Total Cost")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography variant="h6">{formatMoney(totalGross)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {`${formatMoney(totalNet)} ${t("Net")}`}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" loading={loading} onClick={handleSubmit}>
          {t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
